/**
 * Map theme tokens — the single source of truth for the Map app's look,
 * with TWO modes side-by-side: `legacy` (the shipped soft-mint/teal look)
 * and `editorial` (the warm "newspaper-issue" wireframe the founder locked
 * 2026-05-26).
 *
 * Why two modes live here at once: the editorial revamp ships gated behind
 * the per-team `task_editorial_ui` flag and is migrated journey-by-journey
 * (founder cadence: "theme-flag the whole thing — flip on for all teams only
 * when every journey is done; partial state never ships"). So while the
 * migration is in flight, the flag stays OFF for every team and the legacy
 * look is what renders. `useMapTheme()` (see `MapThemeContext.tsx`) resolves
 * which `MapTheme` a Map surface gets; a component is migrated by switching
 * its `import { PALETTE }` / `taskStateStyle` to `useMapTheme().palette` /
 * `.stateStyle` (mechanical — the palette keys match the legacy `PALETTE`).
 *
 * Editorial design system, from the wireframe `tarii.css` (verbatim):
 *   - tone:   --bg #FAF7F0 (warm paper) · --surface #FFF · --ink #1A1612
 *             (warm near-black) · --ink-2/3/4 greys · --rule #E2DCCD hairline
 *   - accent: --teal #3F8B8B (dependency arrows, links) · --teal-soft #DDEDED
 *   - states: ○ #9C9286 not-started · ◐ #4566A0 in-progress ·
 *             ⚠ #C9852D blocked (AMBER, never alarm red) · ● #5E8E5C done
 *             (each with a soft chip background + darker chip text colour).
 *   - type:   Geist (sans) · Instrument Serif italic (masthead/display) ·
 *             Geist Mono (labels, ISSUE tags). Self-hosted (see editorial.css).
 *   - radii:  4 / 6 / 10 / 14.
 *
 * Single-source discipline (`feedback_taskmap_clean_code_standards`): every
 * editorial hex lives here, never scattered inline. State colours stay in the
 * `TaskStateStyle` contract from `tree/state.ts`.
 */

import { PALETTE } from "../tree/palette";
import { type TaskState, type TaskStateStyle, taskStateStyle } from "../tree/state";

export type MapThemeMode = "legacy" | "editorial";

/**
 * The palette keys every Map surface reads. A superset of the legacy
 * `PALETTE` shape (so migrating a component is a mechanical
 * `PALETTE.x` → `theme.palette.x` swap) plus the editorial chrome additions
 * (`bgRecess` / `rule` / `rule2` / `tealSoft`).
 */
export interface MapPalette {
  /** Flat surface behind the tree canvas. */
  canvas: string;
  /** Node-card / panel surface. */
  cardBg: string;
  /** Node-card border (resting). */
  cardBorder: string;
  /** Tree connector line. */
  connector: string;
  /** Primary teal — dependency arrows, links, focus. */
  teal: string;
  /** Darker teal ink (avatar initials on mint, headings). */
  tealInk: string;
  /** Primary heading / node-title ink. */
  ink: string;
  /** Muted body text (ink-3). */
  muted: string;
  /** Secondary ink (ink-2 / rail items). */
  slate: string;
  /** Faint hint text (ink-4). */
  faint: string;
  /** Avatar circle fill A (alternating). */
  avatarA: string;
  /** Avatar circle fill B (alternating + overflow chip). */
  avatarB: string;
  /** Roll-up chip background. */
  rollupBg: string;
  /** Roll-up chip ink. */
  rollupInk: string;
  /** Roll-up progress track. */
  rollupTrack: string;
  /** Roll-up progress fill. */
  rollupFill: string;
  /** Soft border on light controls / coach-mark. */
  softBorder: string;
  /** Editorial: recessed surface — sidebars, footers, card recess (bg-2). */
  bgRecess: string;
  /** Editorial: hairline rule. */
  rule: string;
  /** Editorial: stronger hairline rule. */
  rule2: string;
  /** Editorial: soft teal wash (info banners, teal chips). */
  tealSoft: string;
}

export interface MapFonts {
  /** UI sans. */
  sans: string;
  /** Editorial display serif (masthead, hero headings) — italic in use. */
  serif: string;
  /** Mono — labels, ISSUE tags, dates. */
  mono: string;
}

export interface MapRadii {
  sm: number;
  md: number;
  lg: number;
  xl: number;
}

/** The resolved theme a Map surface renders against. */
export interface MapTheme {
  mode: MapThemeMode;
  palette: MapPalette;
  /** Resolve a task state's style (label / glyph / colours) for this theme. */
  stateStyle: (state: TaskState) => TaskStateStyle;
  fonts: MapFonts;
  radii: MapRadii;
}

// ── Legacy (shipped soft-mint/teal) ───────────────────────────────────────

const LEGACY_PALETTE: MapPalette = {
  ...PALETTE,
  // Editorial-extra keys mapped to their closest legacy equivalents so a
  // migrated component reads sensible values under either mode.
  bgRecess: "#F4F8F6",
  rule: PALETTE.cardBorder,
  rule2: PALETTE.softBorder,
  tealSoft: PALETTE.rollupBg,
};

const LEGACY_FONTS: MapFonts = {
  // Legacy Map inherits the app sans; serif/mono are unused by legacy
  // surfaces (none reference `theme.fonts`) but kept defined for type safety.
  sans: "inherit",
  serif: '"Iowan Old Style", Georgia, serif',
  mono: 'ui-monospace, "SF Mono", Menlo, monospace',
};

// ── Editorial (locked 2026-05-26) ─────────────────────────────────────────

const EDITORIAL_PALETTE: MapPalette = {
  canvas: "#FAF7F0", // --bg, warm paper
  cardBg: "#FFFFFF", // --surface
  cardBorder: "#D3CBB7", // --rule-2 (node card border)
  connector: "#D3CBB7", // --rule-2 (tree edges)
  teal: "#3F8B8B", // --teal
  tealInk: "#2E6868", // teal chip ink
  ink: "#1A1612", // --ink
  muted: "#6B6258", // --ink-3
  slate: "#3B342B", // --ink-2
  faint: "#968C7E", // --ink-4
  avatarA: "#4566A0", // editorial avatars: saturated fill + white text (Wave 1)
  avatarB: "#5E8E5C",
  rollupBg: "#DDEDED", // --teal-soft
  rollupInk: "#2E6868",
  rollupTrack: "#ECE6D6", // --bg-3
  rollupFill: "#5E8E5C", // done sage
  softBorder: "#E2DCCD", // --rule
  bgRecess: "#F4EFE3", // --bg-2
  rule: "#E2DCCD", // --rule
  rule2: "#D3CBB7", // --rule-2
  tealSoft: "#DDEDED", // --teal-soft
};

const EDITORIAL_FONTS: MapFonts = {
  sans: '"Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  serif: '"Instrument Serif", "Iowan Old Style", Georgia, serif',
  mono: '"Geist Mono", ui-monospace, "SF Mono", Menlo, Consolas, monospace',
};

const EDITORIAL_RADII: MapRadii = { sm: 4, md: 6, lg: 10, xl: 14 };

/**
 * The editorial four states — `tarii.css`'s `--st-*` (the glyph/dot colour)
 * paired with the darker `.t-chip` text colour for readability on the soft
 * chip background. `fg` is the chip TEXT colour; `dot` is the standalone
 * glyph/dot colour (saturated). Keyed by the wire `TaskState`
 * (planned=todo · in_progress=prog · blocked=block · done).
 */
const EDITORIAL_STATES: Record<TaskState, TaskStateStyle> = {
  planned: {
    state: "planned",
    label: "Not started",
    glyph: "○",
    fg: "#5E5648",
    chipBg: "#ECE6D6",
    dot: "#9C9286",
  },
  in_progress: {
    state: "in_progress",
    label: "In progress",
    glyph: "◐",
    fg: "#2E4B7E",
    chipBg: "#DCE5F2",
    dot: "#4566A0",
  },
  blocked: {
    state: "blocked",
    label: "Blocked",
    // ⚠ amber, never alarm red (§A9 / wireframe B8.2 / brand firewall).
    glyph: "⚠",
    fg: "#8E5A1A",
    chipBg: "#F5E6CC",
    dot: "#C9852D",
  },
  done: {
    state: "done",
    label: "Done",
    glyph: "●",
    fg: "#3F6A3D",
    chipBg: "#DDE9D9",
    dot: "#5E8E5C",
  },
};

function editorialStateStyle(state: TaskState): TaskStateStyle {
  return EDITORIAL_STATES[state] ?? EDITORIAL_STATES.planned;
}

export const LEGACY_THEME: MapTheme = {
  mode: "legacy",
  palette: LEGACY_PALETTE,
  stateStyle: taskStateStyle,
  fonts: LEGACY_FONTS,
  radii: { sm: 4, md: 6, lg: 8, xl: 12 },
};

export const EDITORIAL_THEME: MapTheme = {
  mode: "editorial",
  palette: EDITORIAL_PALETTE,
  stateStyle: editorialStateStyle,
  fonts: EDITORIAL_FONTS,
  radii: EDITORIAL_RADII,
};
